import React, { Component } from "react"
import { Link } from "react-router-dom";
import axios from "axios";
import "../../assets/css/admin-theme.css"; // unified form-field skin for all /admin/* screens
import { Home, ShoppingCart, Build, ManageAccounts, Inventory, Assessment, PointOfSale, Summarize, GavelSharp } from "@mui/icons-material";
import MenuOpenIcon from '@mui/icons-material/MenuOpen';
import MenuIcon from '@mui/icons-material/Menu';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import IconButton from '@mui/material/IconButton';

import { createTheme, ThemeProvider } from '@mui/material/styles';
import { COLORS, RADIUS, FONT } from '../../Pages/admin/shared/tokens';
import { MenuContext, findCrumb } from '../../Pages/admin/shared/menuCrumb';
import { AuthProvider } from "../../context/AuthContext";
import Avatar from '@mui/material/Avatar';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import Logout from '@mui/icons-material/Logout';
import logoImg from '../../client/assets/images/Logo.png';
import { isMobileNow, watchMobile } from '../../helper/useIsMobile';
import { startCardTableLabels } from '../../helper/cardTableLabels';

const adminTheme = createTheme({
    palette: {
        mode: 'light',
        primary: { main: COLORS.primary, dark: COLORS.primaryStrong, contrastText: COLORS.onPrimary },
        /* ochre accent — use `<Button color="tertiary">` for print / secondary actions */
        tertiary: { main: COLORS.tertiary, contrastText: COLORS.onPrimary },
        background: { default: COLORS.bg, paper: COLORS.surface },
    },
    typography: { fontFamily: FONT },
    components: {
        MuiInputLabel: {
            styleOverrides: {
                root: { fontFamily: FONT, '&.Mui-focused': { color: COLORS.primary } },
            },
        },
        MuiOutlinedInput: {
            styleOverrides: {
                root: {
                    fontFamily: FONT,
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: COLORS.primary, borderWidth: '1px' },
                },
            },
        },
        MuiMenuItem: {
            styleOverrides: {
                root: {
                    fontFamily: FONT, fontSize: '13px',
                    '&:hover': { background: COLORS.primaryTintSoft },
                    '&.Mui-selected': { background: COLORS.primaryTint },
                },
            },
        },
        MuiButton: {
            styleOverrides: {
                root: { textTransform: 'none', borderRadius: `${RADIUS.card}px`, boxShadow: 'none', fontFamily: FONT },
                containedPrimary: { boxShadow: 'none', '&:hover': { boxShadow: 'none', background: COLORS.primaryStrong } },
            },
        },
        MuiChip: {
            styleOverrides: { root: { fontFamily: FONT, borderRadius: `${RADIUS.chip}px` } },
        },
    },
});

/* ─── brand tokens ─────────────────────────────────────────── */
const SIDEBAR_BG   = '#ffffff';
const SIDEBAR_W    = '228px';
const RED          = '#a32f2f';
const RED_DEEP     = '#82161b';
const ACTIVE_BG    = 'rgba(163,47,47,0.10)';   /* flat crimson tint (no gradient) */
const HOVER_BG     = 'rgba(163,47,47,0.06)';
const ACCORD_BG    = '#f1f3ff';
const ACCORD_CHILD = '#f9f9ff';
const TOPBAR_BG    = '#ffffff';
const TEXT_DIM     = '#585f6c';

/* ─── shared inline styles ──────────────────────────────────── */
const sLink = { textDecoration: 'none' };

/* sub-item <p> style */
const subItemStyle = (active) => ({
    margin: 0,
    padding: '9px 16px 9px 44px',
    fontSize: '13px',
    fontWeight: active ? '600' : '500',
    color: active ? RED : TEXT_DIM,
    background: active ? ACTIVE_BG : 'transparent',
    borderLeft: active ? `3px solid ${RED}` : '3px solid transparent',
    cursor: 'pointer',
    letterSpacing: '0.2px',
    transition: 'background 0.18s, color 0.18s',
    fontFamily: "'Inter', sans-serif",
});

/* top-level nav row (Dashboard) */
const topItemStyle = (active) => ({
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    margin: '2px 10px',
    padding: '9px 12px',
    borderRadius: '8px',
    fontSize: '13px',
    fontWeight: active ? '600' : '500',
    color: active ? RED : TEXT_DIM,
    background: active ? ACTIVE_BG : 'transparent',
    boxShadow: active ? `inset 3px 0 0 ${RED}` : 'none',
    cursor: 'pointer',
    letterSpacing: '0.2px',
    transition: 'background 0.18s, color 0.18s',
    fontFamily: "'Inter', sans-serif",
});

class AdminLayout extends Component {
    constructor(props) {
        super(props);
        this.state = {
            // Menu driven by the user's role rights (legacy SP_RE_Admin_GetMenu_ByUserID).
            // null = not loaded yet or the request failed; the hardcoded sidebar below is used
            // as a fallback in that case, so a backend hiccup can never leave a user with no
            // navigation at all.
            menuSections: null,
            menuActions: {},
            // On a phone (< 768px) the sidebar is an overlay drawer, closed until the menu button opens it.
            isMobile: isMobileNow(),
            showSideBar: !isMobileNow(),
            PC_USR_ROLE_ID: localStorage.getItem('PC_USR_ROLE_ID'),
            PC_GRP_ID: localStorage.getItem('PC_GRP_ID'),
            tabName: "dashboard",
            expanded: false,
            anchorEl: null
        }
    }

    handleClick = (event) => { this.setState({ anchorEl: event.currentTarget }) };
    handleClose = () => { this.setState({ anchorEl: null }) };

    stringToColor(string) {
        let hash = 0;
        for (let i = 0; i < string.length; i += 1) {
            hash = string.charCodeAt(i) + ((hash << 5) - hash);
        }
        let color = '#';
        for (let i = 0; i < 3; i += 1) {
            const value = (hash >> (i * 8)) & 0xff;
            color += `00${value.toString(16)}`.slice(-2);
        }
        return color;
    }

    stringAvatar(name) {
        return {
            sx: { bgcolor: RED, width: 34, height: 34, fontSize: '14px', fontWeight: '800' },
            children: `${name.split(' ')[0][0]}`,
        };
    }

    handleChange(panel, evt, isExpanded) { this.setState({ expanded: isExpanded ? panel : false }) }

    componentDidMount() {
        this.setState({ tabName: window.location.pathname })
        this.loadMenu()
        // Crossing the phone breakpoint resets the sidebar to that size's default: a closed
        // drawer on a phone, the open sidebar on anything wider.
        this.unwatchMobile = watchMobile(isMobile => {
            this.setState({ isMobile, showSideBar: !isMobile });
            this.syncCardLabels(isMobile);
        });
        this.syncCardLabels(this.state.isMobile);
        document.addEventListener('keydown', this.handleDrawerKey);
    }

    componentWillUnmount() {
        if (this.unwatchMobile) this.unwatchMobile();
        this.syncCardLabels(false);
        document.removeEventListener('keydown', this.handleDrawerKey);
    }

    // Phone only: column names onto the cells of every table.pc-card-table, modals included
    // (helper/cardTableLabels.js). Never runs at 768px and wider.
    syncCardLabels(on) {
        if (on && !this.stopCardLabels) this.stopCardLabels = startCardTableLabels(document.body);
        if (!on && this.stopCardLabels) { this.stopCardLabels(); this.stopCardLabels = null; }
    }

    // Phone drawer: Escape closes it, and so does following any link inside it.
    handleDrawerKey = (e) => {
        if (e.key === 'Escape' && this.state.isMobile && this.state.showSideBar) this.setState({ showSideBar: false });
    };
    handleDrawerClick = (e) => {
        if (this.state.isMobile && e.target.closest && e.target.closest('a')) this.setState({ showSideBar: false });
    };

    async loadMenu() {
        try {
            const res = await axios.post(`${process.env.REACT_APP_BASE_URL}services/query/PCGetUserMenu.php`, {});
            const d = res.data;
            if (d && d.success === 1 && d.data && Array.isArray(d.data.sections) && d.data.sections.length > 0) {
                const actions = d.data.actions || {};
                this.setState({ menuSections: d.data.sections, menuActions: actions })
                // Persist the role's per-screen action rights so pages can decide whether to
                // OFFER a control (e.g. Approve). Storage follows the pattern the rest of the
                // app already uses for identity (PC_GRP_ID, PC_GRP_ATT, ...).
                //
                // This is an affordance only — never a security boundary. Every approval
                // transition is re-checked server-side against pc_role_rights in
                // helper/Approval.php, so editing localStorage buys nothing.
                try {
                    localStorage.setItem('PC_MENU_ACTIONS', JSON.stringify(actions));
                } catch (e) {
                    /* private mode / quota — the page just falls back to hiding the control */
                }
            }
            // An empty menu (a user with no role) deliberately leaves menuSections null so the
            // fallback renders — matching what those accounts see today rather than blanking them.
        } catch (error) {
            console.error('menu load failed, using built-in sidebar :>> ', error);
        }
    }

    // ICON_CODE is stored as the MUI icon's name so Menu Setup can pick one without a deploy.
    sectionIcon(code) {
        const map = {
            ShoppingCart: <ShoppingCart sx={{ fontSize: '16px' }} />,
            Build: <Build sx={{ fontSize: '16px' }} />,
            ManageAccounts: <ManageAccounts sx={{ fontSize: '16px' }} />,
            Inventory: <Inventory sx={{ fontSize: '16px' }} />,
            Assessment: <Assessment sx={{ fontSize: '16px' }} />,
            PointOfSale: <PointOfSale sx={{ fontSize: '16px' }} />,
            Summarize: <Summarize sx={{ fontSize: '16px' }} />,
            GavelSharp: <GavelSharp sx={{ fontSize: '16px' }} />,
        };
        return map[code] || <Build sx={{ fontSize: '16px' }} />;
    }

    renderDataMenu() {
        return (
            <div>
                {this.state.menuSections.map((sec, i) => (
                    this.renderAccordion(
                        `dpanel${i}`,
                        this.sectionIcon(sec.ICON_CODE),
                        sec.SECTION,
                        <>
                            {sec.links.map(l => this.subLink(l.MENU_URL, l.MENU_URL.split('/').pop(), l.MENU_TEXT))}
                        </>
                    )
                ))}
            </div>
        );
    }

    handleSwitchTab(tabName) {
        switch (tabName) {
            case "profit-loss":
            case "account-balance":
            case "warehouse-balance":
            case "shop-payment":
            case "inventory-report":
            case "get-item-rates":
                this.setState({ tabName, showSideBar: false });
                break;
            default:
                this.setState({ tabName });
                break;
        }
    }

    handleOpensideMenu(bool) { this.setState({ showSideBar: bool }) }

    handleSignOut() {
        ['PC_GRP_ATT','PC_GRP_ID','PC_USR_ROLE_ID','PC_USR_EMAIL','PC_USR_NAME','PC_USR_ID','PC_USR_TOKEN','PC_USR_REFRESH_TOKEN','OTHER_DATA','PC_MENU_ACTIONS']
            .forEach(k => localStorage.removeItem(k));
        window.location.assign('/admin');
    }

    /* ── Accordion group helper ── */
    renderAccordion(panel, icon, label, children) {
        const isOpen = this.state.expanded === panel;
        return (
            <Accordion
                key={panel}
                expanded={isOpen}
                onChange={this.handleChange.bind(this, panel)}
                disableGutters
                elevation={0}
                sx={{
                    background: isOpen ? ACCORD_BG : 'transparent',
                    borderRadius: '10px !important',
                    margin: '2px 10px !important',
                    '&:before': { display: 'none' },
                }}
            >
                <AccordionSummary
                    expandIcon={<ExpandMoreIcon sx={{ color: isOpen ? RED : TEXT_DIM, fontSize: '18px' }} />}
                    sx={{
                        minHeight: '40px !important',
                        padding: '0 12px',
                        borderRadius: '10px',
                        background: isOpen ? ACCORD_BG : 'transparent',
                        '&:hover': { background: HOVER_BG },
                        '& .MuiAccordionSummary-content': { margin: '0 !important', alignItems: 'center' },
                    }}
                >
                    <span style={{ display: 'flex', alignItems: 'center', gap: '10px', color: isOpen ? RED : TEXT_DIM, fontSize: '13px', fontWeight: '600', fontFamily: "'Catamaran', sans-serif", letterSpacing: '0.3px' }}>
                        {icon}
                        {label}
                    </span>
                </AccordionSummary>
                <AccordionDetails sx={{ padding: 0, background: ACCORD_CHILD, borderRadius: '0 0 10px 10px' }}>
                    {children}
                </AccordionDetails>
            </Accordion>
        );
    }

    /* ── sub-link helper ── */
    subLink(to, key, label) {
        const active = this.state.tabName === key || this.state.tabName === to;
        return (
            <Link key={to} style={sLink} to={to}>
                <p
                    style={subItemStyle(active)}
                    onClick={this.handleSwitchTab.bind(this, key)}
                    onMouseEnter={e => { if (!active) { e.currentTarget.style.background = HOVER_BG; e.currentTarget.style.color = RED; } }}
                    onMouseLeave={e => { if (!active) { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = TEXT_DIM; } }}
                >
                    {label}
                </p>
            </Link>
        );
    }

    render() {
        let { tabName, PC_USR_ROLE_ID, PC_GRP_ID, anchorEl, showSideBar, isMobile } = this.state;
        const open = Boolean(anchorEl);
        const dashActive = tabName === 'dashboard' || tabName === '/admin/dashboard';

        return (
            <ThemeProvider theme={adminTheme}>
            <AuthProvider>
                {/* ─── root shell ─── */}
                {/* On a phone the height comes from .pc-shell--mobile (100dvh, so the browser's own toolbars
                    don't hide the bottom of the page); everywhere else it is the original 100vh. */}
                <div className={isMobile ? 'pc-admin pc-shell--mobile' : 'pc-admin'}
                     style={isMobile
                        ? { display: 'flex', overflow: 'hidden', background: '#f9f9ff' }
                        : { display: 'flex', height: '100vh', overflow: 'hidden', background: '#f9f9ff' }}>

                    {/* Phone drawer backdrop — tap outside the menu to close it. */}
                    {isMobile && showSideBar && (
                        <div className="pc-drawer-backdrop" aria-hidden="true"
                             onClick={this.handleOpensideMenu.bind(this, false)} />
                    )}

                    {/* ══════════ SIDEBAR ══════════ */}
                    {showSideBar && (
                        <div
                            className={isMobile ? 'pc-drawer' : undefined}
                            role={isMobile ? 'dialog' : undefined}
                            aria-modal={isMobile ? 'true' : undefined}
                            aria-label={isMobile ? 'Main menu' : undefined}
                            onClick={isMobile ? this.handleDrawerClick : undefined}
                            style={isMobile ? {
                                /* phone: slides over the page from the left (see .pc-drawer in mobile.css) */
                                position: 'fixed', top: 0, bottom: 0, left: 0, zIndex: 1200,
                                width: 'min(280px, 86vw)',
                                background: SIDEBAR_BG,
                                display: 'flex', flexDirection: 'column',
                                overflowY: 'auto', overflowX: 'hidden',
                                boxShadow: '0 10px 30px rgba(0,0,0,0.18)',
                            } : {
                            width: SIDEBAR_W, flexShrink: 0,
                            background: SIDEBAR_BG,
                            borderRight: '1px solid rgba(0,0,0,0.08)',
                            display: 'flex', flexDirection: 'column',
                            height: '100vh', overflowY: 'auto', overflowX: 'hidden',
                        }}>
                            {/* ─ Brand header ─ */}
                            <div style={{
                                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                                padding: '16px 14px',
                                borderBottom: '1px solid rgba(0,0,0,0.08)',
                            }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                    <div style={{
                                        width: '34px', height: '34px', borderRadius: '8px',
                                        background: RED,
                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                        flexShrink: 0,
                                    }}>
                                        <img src={logoImg} alt="logo" style={{ width: '22px', height: '22px', objectFit: 'contain' }} />
                                    </div>
                                    <div>
                                        <div style={{ color: "#000000", fontSize: '13px', fontWeight: '800', lineHeight: 1.1, fontFamily: "'Catamaran', sans-serif" }}>
                                            Phone Clinic <span style={{ color: RED }}>4U</span>
                                        </div>
                                        <div style={{ fontSize: '9px', letterSpacing: '2.5px', color: '#aaa', fontWeight: '600', fontFamily: "'Catamaran', sans-serif" }}>
                                            ADMIN PORTAL
                                        </div>
                                    </div>
                                </div>
                                <IconButton
                                    size="small"
                                    aria-label="Close menu"
                                    onClick={this.handleOpensideMenu.bind(this, false)}
                                    sx={isMobile
                                        ? { color: TEXT_DIM, width: 44, height: 44, '&:hover': { color: RED } }
                                        : { color: TEXT_DIM, '&:hover': { color: RED } }}
                                >
                                    <MenuOpenIcon sx={{ fontSize: isMobile ? '22px' : '18px' }} />
                                </IconButton>
                            </div>

                            {/* ─ Nav items ─ */}
                            <div style={{ flex: 1, paddingTop: '10px', paddingBottom: '16px' }}>

                                {/* Dashboard – always visible */}
                                <Link style={sLink} to="/admin/dashboard">
                                    <div
                                        style={topItemStyle(dashActive)}
                                        onClick={this.handleSwitchTab.bind(this, 'dashboard')}
                                        onMouseEnter={e => { if (!dashActive) { e.currentTarget.style.background = HOVER_BG; e.currentTarget.style.color = RED; } }}
                                        onMouseLeave={e => { if (!dashActive) { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = TEXT_DIM; } }}
                                    >
                                        <Home sx={{ fontSize: '18px' }} /> Dashboard
                                    </div>
                                </Link>

                {/* Role rights drive the menu when they are configured; the hardcoded blocks
                                    below remain as the fallback. */}
                                {this.state.menuSections && this.state.menuSections.length > 0
                                    ? this.renderDataMenu()
                                    : (<>
                                {/* ── Shop role (PC_USR_ROLE_ID 1 or 2, PC_GRP_ID 2) ── */}
                                {(PC_USR_ROLE_ID === "2" || PC_USR_ROLE_ID === "1") && PC_GRP_ID === '2' && (
                                    <div>
                                        {this.renderAccordion('panel1', <ShoppingCart sx={{ fontSize: '16px' }} />, 'Setup', <>
                                            {this.subLink('/admin/job-services', 'job-services', 'Job Services')}
                                            {this.subLink('/admin/vendor-setup', 'vendor-setup', 'Vendor Setup')}
                                        </>)}
                                        {this.renderAccordion('panel3', <GavelSharp sx={{ fontSize: '16px' }} />, 'Repair', <>
                                            {this.subLink('/admin/job', 'job', 'Job')}
                                            {this.subLink('/admin/job-request', 'job-request', 'Job Request')}
                                        </>)}
                                        {this.renderAccordion('panel4', <Inventory sx={{ fontSize: '16px' }} />, 'Inventory', <>
                                            {this.subLink('/admin/devices', 'devices', 'Devices')}
                                            {this.subLink('/admin/items', 'items', 'Items')}
                                        </>)}
                                        {this.renderAccordion('panel5', <PointOfSale sx={{ fontSize: '16px' }} />, 'Sale', <>
                                            {this.subLink('/admin/sale-invoice', 'sale-invoice', 'Item Sale')}
                                            {this.subLink('/admin/sale-device', 'sale-device', 'Device Sale')}
                                        </>)}
                                        {this.renderAccordion('panel6', <ManageAccounts sx={{ fontSize: '16px' }} />, 'Purchase', <>
                                            {this.subLink('/admin/purchase-device', 'purchase-device', 'Purchase Device')}
                                        </>)}
                                        {this.renderAccordion('panel7', <Summarize sx={{ fontSize: '16px' }} />, 'Report', <>
                                            {this.subLink('/admin/get-item-rates', 'get-item-rates', 'Get Item Rate')}
                                            {this.subLink('/admin/inventory-report', 'inventory-report', 'Inventory Available')}
                                        </>)}
                                        {this.renderAccordion('panel8', <Build sx={{ fontSize: '16px' }} />, 'Others', <>
                                            {this.subLink('/admin/refund-device', 'refund-device', 'Refund Device')}
                                        </>)}
                                        {this.renderAccordion('panel9', <ManageAccounts sx={{ fontSize: '16px' }} />, 'Finance', <>
                                            {this.subLink('/admin/weekly-sale', 'weekly-sale', 'Weekly Sale')}
                                        </>)}
                                    </div>
                                )}

                                {/* ── Finance role (PC_USR_ROLE_ID 2, PC_GRP_ID 4) ── */}
                                {PC_USR_ROLE_ID === "2" && PC_GRP_ID === '4' && (
                                    <div>
                                        {this.renderAccordion('panel1', <ManageAccounts sx={{ fontSize: '16px' }} />, 'Finance', <>
                                            {this.subLink('/admin/account-payments', 'account-payments', 'Account Payments')}
                                        </>)}
                                        {this.renderAccordion('panel2', <Assessment sx={{ fontSize: '16px' }} />, 'Report', <>
                                            {this.subLink('/report/profit-loss', 'profit-loss', 'Profit & Loss')}
                                            {this.subLink('/report/account-balance', 'account-balance', 'Account Balance')}
                                            {this.subLink('/report/shop-payment', 'shop-payment', 'Shop Payment')}
                                            {this.subLink('/report/shop-summary', 'shop-summary', 'Shop Summary')}
                                        </>)}
                                    </div>
                                )}

                                {/* ── Super Admin role (PC_USR_ROLE_ID 1, PC_GRP_ID 1) ── */}
                                {PC_USR_ROLE_ID === "1" && PC_GRP_ID === '1' && (
                                    <div>
                                        {this.renderAccordion('panel1', <ShoppingCart sx={{ fontSize: '16px' }} />, 'Setup', <>
                                            {this.subLink('/admin/item-category', 'item-category', 'Item Category')}
                                            {this.subLink('/admin/brand-setup', 'brand-setup', 'Brand Setup')}
                                            {this.subLink('/admin/customer-setup', 'customer-setup', 'Customer Setup')}
                                            {this.subLink('/admin/system-inventory', 'system-inventory', 'Inventory')}
                                            {this.subLink('/admin/system-devices', 'system-devices', 'Devices')}
                                            {this.subLink('/admin/system-services', 'system-services', 'Services')}
                                            {this.subLink('/admin/system-users', 'system-users', 'Users')}
                                            {this.subLink('/admin/system-shop', 'system-shop', 'Shop')}
                                            {this.subLink('/admin/system-pay-account', 'system-pay-account', 'Pay Account')}
                                            {this.subLink('/admin/system-warehouse', 'system-warehouse', 'Warehouse')}
                                            {this.subLink('/admin/roles-setup', 'roles-setup', 'Roles')}
                                            {this.subLink('/admin/menu-setup', 'menu-setup', 'Menu Setup')}
                                            {this.subLink('/admin/role-rights', 'role-rights', 'Role Rights')}
                                            {this.subLink('/admin/shop-transfer-setup', 'shop-transfer-setup', 'Shop Transfer')}
                                        </>)}
                                        {this.renderAccordion('panel2', <ManageAccounts sx={{ fontSize: '16px' }} />, 'Finance', <>
                                            {this.subLink('/admin/weekly-sale', 'weekly-sale', 'Weekly Sale')}
                                            {this.subLink('/admin/shop-payments', 'shop-payments', 'Shop Payments')}
                                            {this.subLink('/admin/account-payments', 'account-payments', 'Account Payments')}
                                        </>)}
                                        {this.renderAccordion('panel3', <Assessment sx={{ fontSize: '16px' }} />, 'Report', <>
                                            {this.subLink('/report/profit-loss', 'profit-loss', 'Profit & Loss')}
                                            {this.subLink('/report/account-balance', 'account-balance', 'Account Balance')}
                                            {this.subLink('/report/shop-payment', 'shop-payment', 'Shop Payment')}
                                            {this.subLink('/report/warehouse-balance', 'warehouse-balance', 'Warehouse Balance')}
                                            {this.subLink('/report/shops-warehouse-delivery', 'shops-warehouse-delivery', 'Shops WH Delivery')}
                                            {this.subLink('/report/shop-summary', 'shop-summary', 'Shop Summary')}
                                        </>)}
                                    </div>
                                )}

                                {/* ── Warehouse role (PC_USR_ROLE_ID 2, PC_GRP_ID 3) ── */}
                                {PC_USR_ROLE_ID === "2" && PC_GRP_ID === '3' && (
                                    <div>
                                        {this.renderAccordion('panel1', <ShoppingCart sx={{ fontSize: '16px' }} />, 'Setup', <>
                                            {this.subLink('/admin/item-category', 'item-category', 'Item Category')}
                                            {this.subLink('/admin/brand-setup', 'brand-setup', 'Brand Setup')}
                                            {this.subLink('/admin/customer-setup', 'customer-setup', 'Customer Setup')}
                                            {this.subLink('/admin/vendor-setup', 'vendor-setup', 'Vendor Setup')}
                                            {this.subLink('/admin/system-inventory', 'system-inventory', 'Inventory')}
                                        </>)}
                                        {this.renderAccordion('panel2', <Inventory sx={{ fontSize: '16px' }} />, 'Inventory', <>
                                            {this.subLink('/admin/warehouse-device', 'warehouse-device', 'Devices')}
                                            {this.subLink('/admin/warehouse-inventory', 'warehouse-inventory', 'Items')}
                                        </>)}
                                        {this.renderAccordion('panel3', <PointOfSale sx={{ fontSize: '16px' }} />, 'Sale', <>
                                            {this.subLink('/admin/sale-device-wh', 'sale-device-wh', 'Device')}
                                            {this.subLink('/admin/sale-item-wh', 'sale-item-wh', 'Item')}
                                        </>)}
                                        {this.renderAccordion('panel4', <ManageAccounts sx={{ fontSize: '16px' }} />, 'Purchase', <>
                                            {this.subLink('/admin/purchase-device-wh', 'purchase-device-wh', 'Device')}
                                        </>)}
                                        {this.renderAccordion('panel5', <Build sx={{ fontSize: '16px' }} />, 'Others', <>
                                            {this.subLink('/admin/refund-device-wh', 'refund-device-wh', 'Refund Device')}
                                            {this.subLink('/admin/refund-item-wh', 'refund-item-wh', 'Refund Item')}
                                        </>)}
                                        {this.renderAccordion('panel6', <Assessment sx={{ fontSize: '16px' }} />, 'Report', <>
                                            {this.subLink('/report/warehouse-balance', 'warehouse-balance', 'Warehouse Balance')}
                                            {this.subLink('/report/shops-warehouse-delivery', 'shops-warehouse-delivery', 'Shops WH Delivery')}
                                        </>)}
                                    </div>
                                )}

                                {/* ── Technician role (PC_USR_ROLE_ID 3, PC_GRP_ID 2) ── */}
                                {PC_USR_ROLE_ID === "3" && PC_GRP_ID === '2' && (
                                    <div>
                                        {this.renderAccordion('panel1', <GavelSharp sx={{ fontSize: '16px' }} />, 'Repair', <>
                                            {this.subLink('/admin/job', 'job', 'Job')}
                                        </>)}
                                        {this.renderAccordion('panel2', <PointOfSale sx={{ fontSize: '16px' }} />, 'Sale', <>
                                            {this.subLink('/admin/sale-invoice', 'sale-invoice', 'Item Sale')}
                                        </>)}
                                        {this.renderAccordion('panel3', <Inventory sx={{ fontSize: '16px' }} />, 'Inventory', <>
                                            {this.subLink('/admin/devices', 'devices', 'Devices')}
                                            {this.subLink('/admin/items', 'items', 'Items')}
                                        </>)}
                                    </div>
                                )}
                                    </>)}
                            </div>

                            {/* ─ Sidebar footer ─ */}
                            <div style={{
                                padding: '12px 14px',
                                borderTop: '1px solid rgba(0,0,0,0.07)',
                            }}>
                                <div style={{ fontSize: '10px', color: '#bbb', letterSpacing: '1.5px', fontFamily: "'Catamaran', sans-serif" }}>
                                    © 2025 PHONE CLINIC 4U
                                </div>
                            </div>
                        </div>
                    )}

                    {/* ══════════ MAIN AREA ══════════ */}
                    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', minWidth: 0 }}>

                        {/* ─ Top navbar ─ */}
                        <div style={{
                            height: '56px', flexShrink: 0,
                            background: TOPBAR_BG,
                            borderBottom: '1px solid rgba(0,0,0,0.08)',
                            display: 'flex', alignItems: 'center',
                            justifyContent: 'space-between',
                            padding: isMobile ? '0 12px 0 4px' : '0 20px',
                        }}>
                            {/* left: hamburger when sidebar hidden */}
                            <div style={isMobile
                                ? { display: 'flex', alignItems: 'center', gap: '4px', flex: 1, minWidth: 0 }
                                : { display: 'flex', alignItems: 'center', gap: '12px' }}>
                                {(!showSideBar || isMobile) && (
                                    <IconButton
                                        size="small"
                                        aria-label="Open menu"
                                        onClick={this.handleOpensideMenu.bind(this, true)}
                                        sx={isMobile
                                            ? { color: TEXT_DIM, width: 44, height: 44, flexShrink: 0, '&:hover': { color: RED } }
                                            : { color: TEXT_DIM, '&:hover': { color: RED } }}
                                    >
                                        <MenuIcon sx={{ fontSize: isMobile ? '24px' : '20px' }} />
                                    </IconButton>
                                )}
                                {/* breadcrumb / page path */}
                                {/* "Report › Warehouse Balance" from the user's own menu. It used to print the raw
                                    path with only the first hyphen replaced ("/REPORT/WAREHOUSE BALANCE"); that is
                                    now only the fallback, for a page with no menu entry. */}
                                <span style={isMobile
                                    ? { fontSize: '11px', color: COLORS.textDim, letterSpacing: '0.06em', fontFamily: FONT, fontWeight: 600, textTransform: 'uppercase', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', minWidth: 0 }
                                    : { fontSize: '12px', color: COLORS.textDim, letterSpacing: '0.08em', fontFamily: FONT, fontWeight: 600, textTransform: 'uppercase' }}>
                                    {(() => {
                                        const c = findCrumb(this.state.menuSections, tabName);
                                        if (c) return `${c.section}  ›  ${c.text}`;
                                        const raw = String(tabName).replace(/^\/(admin|report)\//, '').replace(/-/g, ' ').replace(/\//g, ' › ');
                                        return raw || 'dashboard';
                                    })()}
                                </span>
                            </div>

                            {/* right: user avatar menu */}
                            <div style={isMobile
                                ? { display: 'flex', alignItems: 'center', gap: '12px', flexShrink: 0 }
                                : { display: 'flex', alignItems: 'center', gap: '12px' }}>
                                {/* The avatar's initial stands in for the name on a phone. */}
                                {!isMobile && (
                                <span style={{ fontSize: '13px', color: '#888', fontFamily: "'Catamaran', sans-serif" }}>
                                    {localStorage.getItem('PC_USR_NAME') || 'Admin'}
                                </span>
                                )}
                                <IconButton
                                    onClick={this.handleClick}
                                    size="small"
                                    aria-controls={open ? 'account-menu' : undefined}
                                    aria-haspopup="true"
                                    aria-expanded={open ? 'true' : undefined}
                                    aria-label="Account menu"
                                    sx={isMobile ? { padding: '5px' } : { padding: 0 }}
                                >
                                    <Avatar {...this.stringAvatar(localStorage.getItem("PC_USR_NAME") || 'User')} />
                                </IconButton>
                                <Menu
                                    anchorEl={anchorEl}
                                    id="account-menu"
                                    open={open}
                                    onClose={this.handleClose}
                                    onClick={this.handleClose}
                                    PaperProps={{
                                        elevation: 0,
                                        sx: {
                                            background: '#ffffff',
                                            border: '1px solid #e5e7eb',
                                            borderRadius: '8px',
                                            overflow: 'visible',
                                            filter: 'drop-shadow(0px 10px 15px rgba(0,0,0,0.06))',
                                            mt: 1.5,
                                            '&::before': {
                                                content: '""', display: 'block', position: 'absolute',
                                                top: 0, right: 14, width: 10, height: 10,
                                                bgcolor: '#ffffff', transform: 'translateY(-50%) rotate(45deg)', zIndex: 0,
                                                borderLeft: '1px solid #e5e7eb',
                                                borderTop: '1px solid #e5e7eb',
                                            },
                                        },
                                    }}
                                    transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                                    anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
                                >
                                    <MenuItem
                                        onClick={this.handleSignOut.bind(this)}
                                        sx={{ color: '#141b2b', fontSize: '13px', fontFamily: "'Inter', sans-serif", '&:hover': { background: 'rgba(163,47,47,0.10)', color: '#a32f2f' } }}
                                    >
                                        <ListItemIcon><Logout sx={{ color: RED, fontSize: '18px' }} /></ListItemIcon>
                                        Logout
                                    </MenuItem>
                                </Menu>
                            </div>
                        </div>

                        {/* ─ Page content ─ */}
                        <div
                            className="content"
                            style={{
                                flex: 1, overflowY: 'auto',
                                background: '#f9f9ff',
                                padding: '0',
                            }}
                        >
                            <MenuContext.Provider value={this.state.menuSections}>
                                {this.props.children}
                            </MenuContext.Provider>
                        </div>
                    </div>
                </div>
            </AuthProvider>
            </ThemeProvider>
        );
    }
}

export default AdminLayout;